import {
	type DiningHallSession,
	type FnbDiet,
	MEALS,
	type Meal,
	type MealRequirement,
	TAKEAWAY_ORDER_STATUSES,
	type TakeawayOrderStatus,
} from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { useTranslation } from "react-i18next";
import { currentLocale, intlLocaleFor } from "../../lib/i18n";
import type { StatusGuideGroup } from "../common/status-guide";
import { ClockIcon } from "../transport/icons";
import "./fnb.css";

/**
 * Small read-only pieces every F&B screen shares: the order status chip and
 * dot, a diet tag, a hall's timetable, a diet breakdown. Values only — any
 * sentence around them is a key (`fnb` namespace).
 */

export { Bar, LoadFailed, SkeletonRows, percent } from "../accommodation/parts";
export { EmptyBlock, Section } from "../transport/parts";

/** An order's status (her `.cst` chip): a dot and the label in the status colour; the one-line meaning in the tooltip. */
export function OrderStatusChip({ status, className = "" }: { status: TakeawayOrderStatus; className?: string }) {
	const { t } = useTranslation("fnb");
	return (
		<span className={`fnb-st ${className}`} data-fnb-status={status} title={t(`status.hint.${status}`)}>
			{t(`status.label.${status}`)}
		</span>
	);
}

/** The coloured dot alone (status chips in the toolbar, the Statuses guide). */
export function OrderStatusDot({ status }: { status: TakeawayOrderStatus }) {
	return <span aria-hidden className="fnb-dot" data-fnb-status={status} />;
}

/** A diet's name ("Vegetarian"), for every `people.food_type` value (a scan shows halal too). */
export function useDietLabel() {
	const { t } = useTranslation("fnb");
	return (d: MealRequirement) => t(`diet.${d}`);
}

/**
 * A list in the reader's language ("Vegetarian, Vegan, No beef"), never a
 * hand-typed comma. `type: "unit", style: "short"` = the locale's plain list
 * separator without an "and" (the `narrow` style joins with bare spaces in
 * most locales — that shipped once). The locale goes through
 * `intlLocaleFor` (bm → ms, tet → pt). `lower` lower-cases each item in the
 * locale, for a list inside a sentence ("also vegetarian, vegan").
 */
export function joinList(items: readonly string[], { lower = false }: { lower?: boolean } = {}): string {
	const loc = intlLocaleFor(currentLocale());
	const list = lower ? items.map((x) => x.toLocaleLowerCase(loc)) : items;
	try {
		return new Intl.ListFormat(loc, { style: "short", type: "unit" }).format(list);
	} catch {
		return list.join(", ");
	}
}

/** An order's diet packs ("Vegetarian 2, No beef 1"), or "Standard only". */
export function DietPacks({ diets, noneKey = "orders.standardOnly" }: { diets: ReadonlyArray<{ diet: FnbDiet; packs: number }>; noneKey?: "orders.standardOnly" | "noc.standardPacks" }) {
	const { t } = useTranslation("fnb");
	const { formatNumber: fmt } = useUiStrings();
	const label = useDietLabel();
	if (diets.length === 0) return <span className="text-gms-ink-3">{t(noneKey)}</span>;
	return <span>{joinList(diets.map((d) => t("diet.withCount", { diet: label(d.diet), n: fmt(d.packs) })))}</span>;
}

/** Her `.l3diet` tag: a diet and its count; red when the hall does not serve it. */
export function DietTag({ diet, count, missing = false }: { diet: FnbDiet; count?: number; missing?: boolean }) {
	const { t } = useTranslation("fnb");
	const { formatNumber: fmt } = useUiStrings();
	const label = useDietLabel();
	return (
		<span className="fnb-diet" data-missing={missing || undefined} title={missing ? t("plan.dietMissing") : t("plan.dietServed")}>
			{label(diet)}
			{count !== undefined && <b className="tabular-nums">{fmt(count)}</b>}
		</span>
	);
}

/** "06:00–10:00": a session in the games' wall clock (the caller labels the zone once per screen). */
export function SessionRange({ session, className = "" }: { session: Pick<DiningHallSession, "starts_at" | "ends_at">; className?: string }) {
	const { t } = useTranslation("fnb");
	return <span className={`tabular-nums ${className}`}>{t("time.range", { from: session.starts_at, to: session.ends_at })}</span>;
}

/** Her `.l3tt` timetable: BREAKFAST / LUNCH / DINNER over their hours, three columns. */
export function Timetable({ sessions }: { sessions: readonly DiningHallSession[] }) {
	const { t } = useTranslation("fnb");
	return (
		<span className="grid grid-cols-3 gap-[0.5rem]">
			{MEALS.map((m) => {
				const s = sessions.find((x) => x.meal === m);
				return (
					<span key={m} className="grid gap-[0.125rem]">
						<small className="text-[0.625rem] font-bold tracking-[0.06em] text-gms-ink-3 uppercase">{t(`meal.${m}`)}</small>
						<b className="text-[0.8125rem] font-semibold whitespace-nowrap text-gms-navy-ink">{s ? <SessionRange session={s} /> : "—"}</b>
					</span>
				);
			})}
		</span>
	);
}

/** A meal's name. */
export function useMealLabel() {
	const { t } = useTranslation("fnb");
	return (m: Meal) => t(`meal.${m}`);
}

/** "Times are MYT, the Games' clock": said once above anything showing hall hours or pick-up times. */
export function ZoneNote({ zone, className = "" }: { zone: string; className?: string }) {
	const { t } = useTranslation("fnb");
	return <p className={`text-[0.75rem] text-gms-ink-3 ${className}`}>{t("time.zoneNote", { zone })}</p>;
}

/** The Statuses guide's groups for orders: built from the same status list and dots the pages draw. */
export function useOrderStatusGuide(cutoffHours: number): { title: string; groups: StatusGuideGroup[] } {
	const { t } = useTranslation("fnb");
	return {
		title: t("guide.title"),
		groups: [
			{
				key: "status",
				heading: t("guide.statuses"),
				items: TAKEAWAY_ORDER_STATUSES.map((s) => ({
					key: s,
					label: t(`status.label.${s}`),
					description: t(`status.hint.${s}`),
					marker: <OrderStatusDot status={s} />,
				})),
			},
			{
				key: "tags",
				heading: t("guide.markers"),
				items: [{ key: "late", icon: <ClockIcon />, tone: "warn", label: t("orders.late"), description: t("orders.lateHint", { count: cutoffHours }) }],
			},
		],
	};
}

/** "TO-000123 · late": the reference, and the late flag when set. */
export function OrderRef({ refText, late }: { refText: string; late: boolean }) {
	const { t } = useTranslation("fnb");
	return (
		<span className="text-[0.75rem] text-gms-ink-3 tabular-nums">
			{refText}
			{late && (
				<>
					{" · "}
					<span className="font-semibold text-gms-warn">{t("orders.lateTag")}</span>
				</>
			)}
		</span>
	);
}
